/**
 * BlogListPage Duck
 * Manages state for the blog list page
 */

import { storableError } from '../../util/errors';
import * as log from '../../util/log';
import { apiBaseUrl } from '../../util/api';
import axios from 'axios';

// ================ Action Types ================ //

export const FETCH_BLOG_POSTS_REQUEST = 'app/BlogListPage/FETCH_BLOG_POSTS_REQUEST';
export const FETCH_BLOG_POSTS_SUCCESS = 'app/BlogListPage/FETCH_BLOG_POSTS_SUCCESS';
export const FETCH_BLOG_POSTS_ERROR = 'app/BlogListPage/FETCH_BLOG_POSTS_ERROR';

export const FETCH_PILLAR_PAGES_REQUEST = 'app/BlogListPage/FETCH_PILLAR_PAGES_REQUEST';
export const FETCH_PILLAR_PAGES_SUCCESS = 'app/BlogListPage/FETCH_PILLAR_PAGES_SUCCESS';
export const FETCH_PILLAR_PAGES_ERROR = 'app/BlogListPage/FETCH_PILLAR_PAGES_ERROR';

// ================ Reducer ================ //

const initialState = {
  pillarPages: [],
  pillarFetchInProgress: false,
  pillarFetchError: null,
  posts: [],
  pagination: {
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  },
  fetchInProgress: false,
  fetchError: null,
};

export default function reducer(state = initialState, action = {}) {
  const { type, payload } = action;
  switch (type) {
    case FETCH_BLOG_POSTS_REQUEST:
      return {
        ...state,
        fetchInProgress: true,
        fetchError: null,
      };
    case FETCH_BLOG_POSTS_SUCCESS:
      return {
        ...state,
        posts: payload.posts,
        pagination: payload.pagination,
        fetchInProgress: false,
        fetchError: null,
      };
    case FETCH_BLOG_POSTS_ERROR:
      return {
        ...state,
        fetchInProgress: false,
        fetchError: payload,
      };
    case FETCH_PILLAR_PAGES_REQUEST:
      return {
        ...state,
        pillarFetchInProgress: true,
        pillarFetchError: null,
      };
    case FETCH_PILLAR_PAGES_SUCCESS:
      return {
        ...state,
        pillarPages: payload,
        pillarFetchInProgress: false,
        pillarFetchError: null,
      };
    case FETCH_PILLAR_PAGES_ERROR:
      return {
        ...state,
        pillarFetchInProgress: false,
        pillarFetchError: payload,
      };
    default:
      return state;
  }
}

// ================ Action Creators ================ //

export const fetchBlogPostsRequest = () => ({
  type: FETCH_BLOG_POSTS_REQUEST,
});

export const fetchBlogPostsSuccess = (posts, pagination) => ({
  type: FETCH_BLOG_POSTS_SUCCESS,
  payload: { posts, pagination },
});

export const fetchBlogPostsError = error => ({
  type: FETCH_BLOG_POSTS_ERROR,
  payload: error,
});

export const fetchPillarPagesRequest = () => ({
  type: FETCH_PILLAR_PAGES_REQUEST,
});

export const fetchPillarPagesSuccess = pages => ({
  type: FETCH_PILLAR_PAGES_SUCCESS,
  payload: pages,
});

export const fetchPillarPagesError = error => ({
  type: FETCH_PILLAR_PAGES_ERROR,
  payload: error,
});

// ================ Thunks ================ //

// Helper function to make API calls that work in both browser and SSR
const blogApiRequest = (path, marketplaceRootURL) => {
  const baseUrl = apiBaseUrl(marketplaceRootURL);
  const url = `${baseUrl}${path}`;
  
  return axios.get(url, {
    withCredentials: true,
    headers: { 'Content-Type': 'application/json' },
  }).then(response => response.data)
    .catch(error => {
      if (error.response) {
        const e = new Error(error.response.data?.message || `HTTP error! status: ${error.response.status}`);
        Object.assign(e, error.response.data || {});
        throw e;
      }
      throw error;
    });
};

export const fetchBlogPosts = (params = {}, config = {}) => (dispatch, getState) => {
  const { page = 1, limit = 10, category, tag, search, page_type, page_subtype, status } = params;
  const marketplaceRootURL = config?.marketplaceRootURL;
  
  dispatch(fetchBlogPostsRequest());

  const queryParams = new URLSearchParams();
  queryParams.append('page', page);
  queryParams.append('limit', limit);
  if (category) queryParams.append('category', category);
  if (tag) queryParams.append('tag', tag);
  if (search) queryParams.append('search', search);
  if (page_type) queryParams.append('page_type', page_type);
  if (page_subtype) queryParams.append('page_subtype', page_subtype);
  if (status) queryParams.append('status', status);

  return blogApiRequest(`/api/blog?${queryParams.toString()}`, marketplaceRootURL)
    .then(data => {
      console.log(`[BlogListPage] API returned ${data.posts?.length || 0} posts`);
      dispatch(fetchBlogPostsSuccess(data.posts || [], data.pagination || {}));
      return data;
    })
    .catch(e => {
      log.error(e, 'fetch-blog-posts-failed', { params });
      const error = storableError(e);
      dispatch(fetchBlogPostsError(error));
      throw error;
    });
};

export const fetchPillarPages = (params = {}, config = {}) => (dispatch, getState) => {
  const { limit = 48 } = params;
  const marketplaceRootURL = config?.marketplaceRootURL;

  dispatch(fetchPillarPagesRequest());

  const queryParams = new URLSearchParams();
  queryParams.append('page', 1);
  queryParams.append('limit', limit);
  queryParams.append('page_type', 'page');
  queryParams.append('page_subtype', 'pillar');
  // Keep API behavior explicit: don't rely on server default.
  queryParams.append('status', 'published');

  return blogApiRequest(`/api/blog?${queryParams.toString()}`, marketplaceRootURL)
    .then(data => {
      dispatch(fetchPillarPagesSuccess(data.posts || []));
      return data;
    })
    .catch(e => {
      log.error(e, 'fetch-pillar-pages-failed', { params });
      const error = storableError(e);
      dispatch(fetchPillarPagesError(error));
      throw error;
    });
};

// ================ Selectors ================ //

export const getBlogPosts = state => state.BlogListPage?.posts || [];
export const getBlogPagination = state => state.BlogListPage?.pagination || {
  page: 1,
  limit: 10,
  total: 0,
  totalPages: 0
};
export const getBlogFetchInProgress = state => state.BlogListPage?.fetchInProgress || false;
export const getBlogFetchError = state => state.BlogListPage?.fetchError || null;

export const getPillarPages = state => state.BlogListPage?.pillarPages || [];
export const getPillarFetchInProgress = state => state.BlogListPage?.pillarFetchInProgress || false;
export const getPillarFetchError = state => state.BlogListPage?.pillarFetchError || null;

// ================ Load Data ================ //

export const loadData = (params, search, config) => (dispatch, getState) => {
  const queryParams = new URLSearchParams(search);
  const page = parseInt(queryParams.get('page') || '1', 10);
  const category = queryParams.get('category');
  const tag = queryParams.get('tag');
  const searchQuery = queryParams.get('search');

  return Promise.all([
    dispatch(
      fetchPillarPages(
        {
          limit: 48,
        },
        config
      )
    ),
    dispatch(
      fetchBlogPosts(
        {
          page,
          category,
          tag,
          search: searchQuery,
        },
        config
      )
    ),
  ]);
};

